import { Injectable, signal } from '@angular/core';
import { PlatformResponse } from 'src/app/interfaces/webservices-interface';
import { CustomerModel } from 'src/app/models/customer-model';
import { ConfigService } from '../config/config.service';
import { UserSessionService } from '../user-session/user-session.service';
import { LobbyNavigator } from './lobby-navigator.service';
import { GamesService } from './games.service';
import { LobbyApiService } from './lobby-api.service';
import { EventsService } from '../event/event.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LobbyInitService {

  private localKeywords: any[] = [];
  private userStatus: any = null;
  public authBasedKeywords: any;
  public customer: CustomerModel;
 
  public lobbyList: any;
  public lobbyName: any;
  public bottomLobbyName: any;
  public filterKeyWordList = [];
  public filterHiddenKeyWordList = [];
  public gamesSubject = new BehaviorSubject<any[]>([]);
  public games$ = this.gamesSubject.asObservable();
  public bottomGamesSubject = new BehaviorSubject<any[]>([]);
  public bottomGames$ = this.bottomGamesSubject.asObservable();

  public keywordsSubject = new BehaviorSubject<any[]>([]);
  public keywords$ = this.keywordsSubject.asObservable();
  
  constructor(
    private config: ConfigService,
    private gamesService: GamesService,
    private lobbyApi: LobbyApiService,
    private lobbyNavigator: LobbyNavigator,
    private userSession: UserSessionService,
    private eventService: EventsService
  ) {
    this.initialize();
    this.userSession.getUserStatus().subscribe((userStatus) => {
      this.userStatus = userStatus;
      this.keywordsBasedOnAuth();
    });
  }

  private async initialize(): Promise<void> {
    await this.config.isLoaded();

    this.authBasedKeywords = this.config.getConfig()["FEATURE_AUTH_KEYWORDS"] ?? [
      "FAVOURITEGAMESCASINODESKTOPWEBMAIN",
      "FAVOURITEGAMESCASINOMOBILEWEBMAIN"
    ];

    this.initLobby();
  }

  initLobby(): void {
    this.lobbyApi.getLobbiesKeywords().subscribe({
      next: (response: PlatformResponse<any>) => {
        if (response.result && response.payload) {
          this.lobbyList = response.payload;
          this.eventService.publish("event:mobile-tabs-updated",this.lobbyList);  
          this.createMainLobby(this.lobbyList);
        }
      },
      error: (error) => {
        console.error('Error initializing lobbies keywords:', error);
      }
    });
  }

  private createMainLobby(lobbyList: any): void {
    const lobbyFilter: string = this.config?.getConfig()?.FEATURE_LOBBYLIST_FILTER ?? "CASINO";
    const lobbyTempList = lobbyList?.filter((m) => !!~m?.name?.indexOf(lobbyFilter));
    this.lobbyName = lobbyTempList?.filter((l) => {
      if ("placement" in l) {
        return l.placement === "MAIN";
      }
    })[0]?.name;
    const bottomLobbies = lobbyList.filter((l) => l.placement === 'BOTTOM')?.[0]?.name;
    if(bottomLobbies.length > 0){
      this.lobbyNavigator.setBottomLobbyName(bottomLobbies);
      this.getBottomGames(bottomLobbies);
    }
    this.lobbyNavigator.setLobbySubject(this.lobbyName);
    this.gamesService.setLobbyName(this.lobbyName);
    this.getGames(this.lobbyName);
    this.getLobbyKeywords(this.lobbyName);
  }

  private getLobbyKeywords(lobbyName: string): any {
    this.lobbyApi.getKeywords(lobbyName).subscribe({
      next: (response) => {
        if (response?.result) {
          this.localKeywords = response?.payload;
          let keywordListWithoutHidden = this.localKeywords.filter((k) => k.hide_keyword === false);
          let keywordListWitHidden = this.localKeywords.filter((k) => k.hide_keyword === true);
          this.filterKeyWordList = keywordListWithoutHidden;
          this.filterHiddenKeyWordList = keywordListWitHidden;
          this.lobbyNavigator.setKeywordList(this.filterKeyWordList);
          this.lobbyNavigator.setHiddenKeywordList(this.filterHiddenKeyWordList);
          this.keywordsBasedOnAuth();
        }
      },
      error: (error) => {
        console.error('Failed to fetch keywords', error);
      },
      complete: () => {
        console.log('Keyword fetching complete');
      }
    });
  }

  private keywordsBasedOnAuth(): void {
    const filteredKeys = this.filterKeyWordList?.filter((f) => !this.authBasedKeywords?.includes(f.keyword_code));
    if (this.filterKeyWordList?.length > 0 && this.userStatus !== undefined) {
      let keys: any[] = [];
      this.userStatus === null ? (keys = filteredKeys) : (keys = this.filterKeyWordList);
      this.keywordsSubject.next(keys);
      this.eventService.publish("event:mobile-search-keyword-updated");  
    }
  }

  private getGames(lobbyName: string): void {
    this.lobbyApi.getGamesByLobbyName(lobbyName).subscribe({
      next: (response) => {
        if (response?.result) {
          const payload: any = response?.payload;
          this.gamesSubject.next(payload);
        }
      }, error: (error) => {
        console.error('Failed to fetch games', error);
      },
      complete: () => {
        console.log('games fetching complete');
      }
    });
  }

  private getBottomGames(bottomLobbyName: string): void {
    this.lobbyApi.getGamesByLobbyName(bottomLobbyName).subscribe({
      next: (response) => {
        if (response?.result) {
          this.bottomGamesSubject.next(response?.payload);
        }
      },
      error: (error) => {
        console.error('Failed to fetch bottom games', error);
      }
    });
  }
}
